// SPDX-License-Identifier: MIT
// useDuckDBPreload Hook - Pre-initializes DuckDB when datasets are loaded
// This reduces perceived latency when opening the SQL panel

import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { DuckDBService } from '../services/duckdb-service';

// Selector for Kepler.gl datasets
const selectKeplerDatasets = (state: any) => {
  try {
    return state?.keplerGl?.map?.visState?.datasets ?? {};
  } catch {
    return {};
  }
};

/**
 * Hook that pre-initializes DuckDB when datasets are first loaded.
 * This ensures DuckDB is ready before the user opens the SQL panel,
 * reducing perceived latency.
 *
 * Call this hook at the app level (e.g., in App.tsx) to enable preloading.
 */
export function useDuckDBPreload(): void {
  const keplerDatasets = useSelector(selectKeplerDatasets);
  const hasInitializedRef = useRef(false);
  const isInitializingRef = useRef(false);

  useEffect(() => {
    // Only initialize once when datasets first become available
    const datasetIds = Object.keys(keplerDatasets);
    if (datasetIds.length === 0) return;
    if (hasInitializedRef.current) return;
    if (isInitializingRef.current) return;

    isInitializingRef.current = true;

    const preloadDuckDB = async () => {
      try {
        console.log('[DuckDB Preload] Starting pre-initialization...');
        await DuckDBService.getInstance();
        hasInitializedRef.current = true;
        console.log('[DuckDB Preload] Pre-initialization complete');
      } catch (error) {
        console.warn('[DuckDB Preload] Pre-initialization failed:', error);
      } finally {
        isInitializingRef.current = false;
      }
    };

    preloadDuckDB();
  }, [keplerDatasets]);
}

export default useDuckDBPreload;
